import React, { useState, useRef, useLayoutEffect, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiLayers, FiPackage, FiTrendingUp, FiDatabase, FiEye, FiCheck, FiClock, FiTarget, FiBarChart, FiSearch } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { TOKENIZER_PLATFORM_SECTORS } from '../../api/smartTokenizerMappers';
import { tokenizersApi } from '../../api/smartTokenizerApi';
import { filterAssetClasses } from '../../utils/filterAssetClasses';
import {
  hasAssetClassDeployedContract,
  shouldShowAssetClassContractCard,
  shouldShowAssetClassContractDeploymentUi
} from '../../utils/assetClassContractUiAccess';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import AssetClassCreationWizard from './AssetClassCreationWizard';
import BatchMintingWizard from './BatchMintingWizard';
import AssetClassDetailsModal from './AssetClassDetailsModal';
import AssetClassDeployContractModal from './AssetClassDeployContractModal';
import AssetClassCustodyVaultDeployModal from './AssetClassCustodyVaultDeployModal';
import AssetClassFractionTokenDeployModal from './AssetClassFractionTokenDeployModal';
import { AssetClassContractCardBlock } from './AssetClassContractCardBlock';
import { AssetClassCustodyVaultBlock } from './AssetClassCustodyVaultBlock';
import { AssetClassFractionTokenBlock } from './AssetClassFractionTokenBlock';

const ManagerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 3rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #4facfe, #00f2fe, #4facfe);
    animation: shimmer 3s infinite linear;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  font-weight: 800;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;

  .stat-icon {
    font-size: 2rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.primary ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.primary ? 'linear-gradient(135deg, #3d8bfe, #00d4fe)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const TemplatesSection = styled.div`
  margin-bottom: 3rem;
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const TemplateCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.1);
  }

  .template-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .template-info {
    flex: 1;
  }

  .template-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .template-category {
    color: #4facfe;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .template-description {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  .template-specs {
    background: rgba(79, 172, 254, 0.05);
    border: 1px solid rgba(79, 172, 254, 0.2);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;

    .specs-title {
      font-weight: 600;
      color: #4facfe;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    .specs-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .spec-tag {
      background: rgba(79, 172, 254, 0.1);
      border: 1px solid rgba(79, 172, 254, 0.3);
      border-radius: 4px;
      padding: 0.25rem 0.5rem;
      font-size: 0.8rem;
      color: #4facfe;
    }
  }

  .template-actions {
    display: flex;
    gap: 0.5rem;
  }

  .template-action {
    padding: 0.5rem 1rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 6px;
    color: #4facfe;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(79, 172, 254, 0.2);
    }
  }
`;

const AssetClassesSection = styled.div`
  margin-bottom: 3rem;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FilterSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
  max-width: 420px;
  color: #94a3b8;
  svg {
    flex-shrink: 0;
  }
`;

const FilterTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterSectorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  label {
    color: #94a3b8;
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const FilterSelect = styled.select`
  padding: 0.6rem 0.75rem;
  min-width: 200px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterMeta = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  @media (min-width: 720px) {
    margin-left: auto;
  }
`;

const AssetClassGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const AssetClassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.1);
  }

  .class-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1.5rem;
  }

  .class-info {
    flex: 1;
  }

  .class-name {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .class-details {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .badge-standard {
    display: inline-block;
    margin-top: 0.35rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #c4b5fd;
    border: 1px solid rgba(196, 181, 253, 0.45);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
  }

  .class-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
  }

  .metric {
    text-align: center;

    .metric-value {
      font-size: 1.2rem;
      font-weight: 600;
      color: #4facfe;
      margin-bottom: 0.25rem;
    }

    .metric-label {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }

  .class-actions {
    display: flex;
    gap: 0.75rem;
  }

  .class-action {
    flex: 1;
    padding: 0.75rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 8px;
    color: #4facfe;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:hover {
      background: rgba(79, 172, 254, 0.2);
    }

    &.primary {
      background: linear-gradient(135deg, #4facfe, #00f2fe);
      color: #fff;
      border: none;

      &:hover {
        background: linear-gradient(135deg, #3d8bfe, #00d4fe);
      }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        background: rgba(79, 172, 254, 0.22);
        color: rgba(255, 255, 255, 0.75);
      }

      &:disabled:hover {
        background: rgba(79, 172, 254, 0.22);
        transform: none;
      }
    }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const SchemaModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-shrink: 0;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    color: #fff;
    font-size: 1.35rem;
    margin: 0;
    line-height: 1.25;
  }

  .meta {
    color: #94a3b8;
    font-size: 0.85rem;
    margin-top: 0.4rem;
    line-height: 1.4;
  }
`;

/** Schema viewer only: single scroll region, no outer+inner double scroll */
const SchemaViewModalSurface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 720px;
  max-height: min(88vh, 800px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.25rem;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
`;

const SchemaScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.45);

  scrollbar-width: thin;
  scrollbar-color: rgba(79, 172, 254, 0.45) rgba(255, 255, 255, 0.06);

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.04);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.35);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: padding-box;

    &:hover {
      background: rgba(79, 172, 254, 0.55);
      background-clip: padding-box;
    }
  }
`;

const SchemaCode = styled.pre`
  margin: 0;
  padding: 1.1rem 1.25rem 1.25rem;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace;
  font-size: 0.8125rem;
  line-height: 1.55;
  white-space: pre;
  tab-size: 2;
  color: #e2e8f0;

  .json-hl-key {
    color: #7dd3fc;
  }
  .json-hl-str {
    color: #86efac;
  }
  .json-hl-num {
    color: #fcd34d;
  }
  .json-hl-bool {
    color: #c4b5fd;
  }
  .json-hl-null {
    color: #94a3b8;
    font-style: italic;
  }
`;

function jsonStringToHighlightedHtml(json) {
  return json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /("(\\u[\dA-Fa-f]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = 'json-hl-num';
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? 'json-hl-key' : 'json-hl-str';
        } else if (/true|false/.test(match)) {
          cls = 'json-hl-bool';
        } else if (/null/.test(match)) {
          cls = 'json-hl-null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
}

const ModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

function AssetClassManager() {
  const { 
    assetClasses, 
    assetClassTemplates, 
    statistics, 
    getMarketPairForAssetClass,
    loadAssetClasses,
  } = useAssetClass();

  const { isAdmin, currentIssuer } = useTokenIssuer();
  const authorizedTokenizerId = currentIssuer?.authorizedTokenizerId ?? null;

  const [activeModal, setActiveModal] = useState(null);
  const [creationWizardSeedTemplate, setCreationWizardSeedTemplate] = useState(null);
  const [selectedAssetClass, setSelectedAssetClass] = useState(null);
  const [viewSchemaTemplate, setViewSchemaTemplate] = useState(null);
  const [marketModalClass, setMarketModalClass] = useState(null);
  const [activeClassSearchQuery, setActiveClassSearchQuery] = useState('');
  const [activeClassSectorFilter, setActiveClassSectorFilter] = useState('');
  const [deployModalClass, setDeployModalClass] = useState(null);
  const [custodyVaultDeploy, setCustodyVaultDeploy] = useState(null);
  const [fractionTokenDeploy, setFractionTokenDeploy] = useState(null);
  const [custodyTkPick, setCustodyTkPick] = useState({});
  const [fractionTkPick, setFractionTkPick] = useState({});
  const [tokenizerPickerOptions, setTokenizerPickerOptions] = useState([]);
  const schemaScrollRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    tokenizersApi
      .list()
      .then(({ data }) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        const approved = rows.filter((t) => {
          const s = t.status ?? t.Status;
          return s === 'Approved';
        });
        setTokenizerPickerOptions(
          approved.map((t) => {
            const id = String(t.id ?? t.Id ?? '');
            const reg = t.registrationNumber ?? t.RegistrationNumber ?? '';
            const name = t.organizationName ?? t.OrganizationName ?? id;
            return { id, label: reg ? `${reg} · ${name}` : name };
          })
        );
      })
      .catch(() => {
        if (!cancelled) setTokenizerPickerOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const filteredActiveAssetClasses = useMemo(
    () =>
      filterAssetClasses(assetClasses, {
        searchQuery: activeClassSearchQuery,
        sectorFilter: activeClassSectorFilter
      }),
    [assetClasses, activeClassSearchQuery, activeClassSectorFilter]
  );

  const schemaViewPayload = useMemo(() => {
    if (!viewSchemaTemplate) {
      return { jsonStr: '', html: '' };
    }
    const jsonStr = JSON.stringify(
      {
        id: viewSchemaTemplate.id,
        standardSchema: viewSchemaTemplate.standardSchema,
        composability: viewSchemaTemplate.composability,
        tradingUnit: viewSchemaTemplate.tradingUnit,
        marketSymbol: viewSchemaTemplate.marketSymbol,
        classType: viewSchemaTemplate.classType,
        category: viewSchemaTemplate.category
      },
      null,
      2
    );
    return { jsonStr, html: jsonStringToHighlightedHtml(jsonStr) };
  }, [viewSchemaTemplate]);

  useLayoutEffect(() => {
    if (!viewSchemaTemplate) return;
    const el = schemaScrollRef.current;
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }, [viewSchemaTemplate, schemaViewPayload.jsonStr]);

  const openModal = (modalType, assetClass = null) => {
    setSelectedAssetClass(assetClass);
    setActiveModal(modalType);
  };

  const openCreateCustomModal = (template = null) => {
    setCreationWizardSeedTemplate(template);
    setActiveModal('createCustom');
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedAssetClass(null);
    setCreationWizardSeedTemplate(null);
  };

  return (
    <ManagerContainer>
      <HeroSection>
        <HeroTitle>Asset Class Manager</HeroTitle>
        <HeroSubtitle>
          Create reusable asset classes for batch minting and spot market trading. 
          Define standardized schemas for consistent tokenization across multiple assets.
        </HeroSubtitle>
      </HeroSection>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <FiLayers className="stat-icon" />
          <div className="stat-value">{statistics.totalClasses}</div>
          <div className="stat-label">Asset Classes</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <FiPackage className="stat-icon" />
          <div className="stat-value">{statistics.totalBatches}</div>
          <div className="stat-label">Total Batches</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <FiTrendingUp className="stat-icon" />
          <div className="stat-value">{statistics.activeMarkets}</div>
          <div className="stat-label">Active Markets</div>
        </StatCard>
      </StatsGrid>

      <TemplatesSection>
        <ActionsSection>
          <SectionTitle>
            <FiTarget className="icon" />
            Asset Class Templates
          </SectionTitle>
          <ActionButton primary onClick={() => openCreateCustomModal(null)}>
            <FiPlus />
            Create Custom Class
          </ActionButton>
        </ActionsSection>

        <TemplatesGrid>
          {Object.values(assetClassTemplates).map((template, index) => (
            <TemplateCard
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="template-header">
                <div className="template-info">
                  <div className="template-name">{template.name}</div>
                  <div className="template-category">{template.category}</div>
                </div>
              </div>
              
              <div className="template-description">
                Standardized {template.classType} for {template.tradingUnit}-based trading
              </div>

              <div className="template-specs">
                <div className="specs-title">Required Fields</div>
                <div className="specs-list">
                  {template.standardSchema.required.map(field => (
                    <span key={field} className="spec-tag">{field}</span>
                  ))}
                </div>
              </div>

              <div className="template-actions">
                <button
                  type="button"
                  className="template-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateCustomModal(template);
                  }}
                >
                  <FiPlus /> Create Class
                </button>
                <button
                  type="button"
                  className="template-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewSchemaTemplate(template);
                  }}
                >
                  <FiEye /> View Schema
                </button>
              </div>
            </TemplateCard>
          ))}
        </TemplatesGrid>
      </TemplatesSection>

      <AssetClassesSection>
        <ActionsSection>
          <SectionTitle>
            <FiDatabase className="icon" />
            Active Asset Classes
          </SectionTitle>
        </ActionsSection>

        <FilterBar>
          <FilterSearchWrap>
            <FiSearch aria-hidden />
            <FilterTextInput
              type="search"
              value={activeClassSearchQuery}
              onChange={(e) => setActiveClassSearchQuery(e.target.value)}
              placeholder="Search name, code, sector, symbol…"
              aria-label="Search active asset classes"
            />
          </FilterSearchWrap>
          <FilterSectorWrap>
            <label htmlFor="asset-manager-active-class-sector-filter">Sector</label>
            <FilterSelect
              id="asset-manager-active-class-sector-filter"
              value={activeClassSectorFilter}
              onChange={(e) => setActiveClassSectorFilter(e.target.value)}
            >
              <option value="">All sectors</option>
              {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </FilterSelect>
          </FilterSectorWrap>
          {assetClasses.length > 0 && (
            <FilterMeta>
              Showing {filteredActiveAssetClasses.length} of {assetClasses.length}
            </FilterMeta>
          )}
        </FilterBar>

        {assetClasses.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem 2rem', 
            color: '#a0a0a0' 
          }}>
            <FiLayers style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
            <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No Asset Classes Yet</h3>
            <p>Create your first asset class from a template or build a custom one</p>
          </div>
        ) : filteredActiveAssetClasses.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: '#a0a0a0'
            }}
          >
            <FiSearch style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
            <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No matching asset classes</h3>
            <p>Try a different search or set Sector to “All sectors”.</p>
          </div>
        ) : (
          <AssetClassGrid>
            {filteredActiveAssetClasses.map((assetClass, index) => {
              const canAddBatch = hasAssetClassDeployedContract(assetClass);
              return (
              <AssetClassCard
                key={assetClass.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="class-header">
                  <div className="class-info">
                    <div className="class-name">{assetClass.name}</div>
                    <div className="class-details">
                      {assetClass.marketSymbol} • {assetClass.tradingUnit} • {assetClass.classType}
                    </div>
                    {assetClass.isPlatformStandard && (
                      <span className="badge-standard">Standard asset class</span>
                    )}
                  </div>
                </div>

                <div className="class-metrics">
                  <div className="metric">
                    <div className="metric-value">{assetClass.totalBatches}</div>
                    <div className="metric-label">Batches</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value">{assetClass.totalSupply}</div>
                    <div className="metric-label">Total Supply</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value">
                      {assetClass.isActive ? <FiCheck style={{ color: '#00ff88' }} /> : <FiClock />}
                    </div>
                    <div className="metric-label">Status</div>
                  </div>
                </div>

                {shouldShowAssetClassContractCard(assetClass, {
                  isAdmin,
                  authorizedTokenizerId
                }) && (
                  <AssetClassContractCardBlock
                    assetClass={assetClass}
                    actionClassName="class-action"
                    onDeployClick={() => setDeployModalClass(assetClass)}
                    canDeploy={shouldShowAssetClassContractDeploymentUi(assetClass, {
                      isAdmin,
                      authorizedTokenizerId
                    })}
                  />
                )}

                {assetClass.tokenStandard === 'ERC721' && hasAssetClassDeployedContract(assetClass) && (
                  <AssetClassCustodyVaultBlock
                    assetClass={assetClass}
                    isAdmin={isAdmin}
                    authorizedTokenizerId={authorizedTokenizerId}
                    selectedCustodyTokenizerId={isAdmin ? custodyTkPick[assetClass.id] || '' : ''}
                    onCustodyTokenizerSelectionChange={
                      isAdmin
                        ? (tid) => setCustodyTkPick((m) => ({ ...m, [assetClass.id]: tid }))
                        : undefined
                    }
                    tokenizerPickerOptions={isAdmin ? tokenizerPickerOptions : []}
                    actionClassName="class-action"
                    onOpenCustodyVaultModal={(tid) => {
                      if (!tid) return;
                      setCustodyVaultDeploy({ assetClass, authorizedTokenizerId: tid });
                    }}
                  />
                )}

                {assetClass.tokenStandard === 'ERC721' && hasAssetClassDeployedContract(assetClass) && (
                  <AssetClassFractionTokenBlock
                    assetClass={assetClass}
                    isAdmin={isAdmin}
                    authorizedTokenizerId={authorizedTokenizerId}
                    selectedFractionTokenizerId={isAdmin ? fractionTkPick[assetClass.id] || '' : ''}
                    onFractionTokenizerSelectionChange={
                      isAdmin
                        ? (tid) => setFractionTkPick((m) => ({ ...m, [assetClass.id]: tid }))
                        : undefined
                    }
                    tokenizerPickerOptions={isAdmin ? tokenizerPickerOptions : []}
                    actionClassName="class-action"
                    onOpenFractionTokenModal={(tid) => {
                      if (!tid) return;
                      setFractionTokenDeploy({ assetClass, authorizedTokenizerId: tid });
                    }}
                  />
                )}

                <div className="class-actions">
                  <button
                    type="button"
                    className="class-action primary"
                    disabled={!canAddBatch}
                    title={
                      canAddBatch
                        ? undefined
                        : 'Deploy the smart contract for this asset class before adding batches.'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canAddBatch) return;
                      openModal('addBatch', assetClass);
                    }}
                  >
                    <FiPlus />
                    Add Batch
                  </button>
                  <button
                    type="button"
                    className="class-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal('viewDetails', assetClass);
                    }}
                  >
                    <FiEye />
                    View
                  </button>
                  <button
                    type="button"
                    className="class-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMarketModalClass(assetClass);
                    }}
                  >
                    <FiBarChart />
                    Market
                  </button>
                </div>
              </AssetClassCard>
            );
            })}
          </AssetClassGrid>
        )}
      </AssetClassesSection>

      <AnimatePresence>
        {deployModalClass &&
          shouldShowAssetClassContractDeploymentUi(deployModalClass, {
            isAdmin,
            authorizedTokenizerId
          }) && (
            <AssetClassDeployContractModal
              assetClass={deployModalClass}
              onClose={() => setDeployModalClass(null)}
              onCompleted={() => loadAssetClasses()}
            />
          )}
        {custodyVaultDeploy && (
          <AssetClassCustodyVaultDeployModal
            assetClass={
              assetClasses.find((c) => c.id === custodyVaultDeploy.assetClass.id) ?? custodyVaultDeploy.assetClass
            }
            authorizedTokenizerId={custodyVaultDeploy.authorizedTokenizerId}
            onClose={() => setCustodyVaultDeploy(null)}
            onCompleted={() => loadAssetClasses()}
          />
        )}
        {fractionTokenDeploy
          ? React.createElement(AssetClassFractionTokenDeployModal, {
              assetClass:
                assetClasses.find((c) => c.id === fractionTokenDeploy.assetClass.id) ?? fractionTokenDeploy.assetClass,
              authorizedTokenizerId: fractionTokenDeploy.authorizedTokenizerId,
              onClose: () => setFractionTokenDeploy(null),
              onCompleted: () => loadAssetClasses()
            })
          : null}
      </AnimatePresence>

      {/* Modals — viewDetails is separate so AssetClassDetailsModal (position:fixed) is not inside a transformed parent */}
      <AnimatePresence>
        {activeModal && activeModal !== 'viewDetails' && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {activeModal === 'createCustom' && (
                <AssetClassCreationWizard
                  key={creationWizardSeedTemplate ? `tpl-${creationWizardSeedTemplate.id}` : 'custom-blank'}
                  onClose={closeModal}
                  initialFromTemplate={creationWizardSeedTemplate}
                />
              )}
              {activeModal === 'addBatch' && (
                <BatchMintingWizard 
                  assetClass={selectedAssetClass}
                  onClose={closeModal} 
                />
              )}
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeModal === 'viewDetails' && selectedAssetClass && (
          <AssetClassDetailsModal assetClass={selectedAssetClass} onClose={closeModal} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {marketModalClass && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMarketModalClass(null)}
          >
            <SchemaViewModalSurface
              initial={{ scale: 0.92, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 12 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '480px' }}
            >
              <SchemaModalHeader>
                <div>
                  <h2>Spot market</h2>
                  <div className="meta">
                    {marketModalClass.name} · {marketModalClass.marketSymbol} · {marketModalClass.tradingUnit || 'unit'}
                  </div>
                </div>
                <ActionButton type="button" onClick={() => setMarketModalClass(null)}>
                  Close
                </ActionButton>
              </SchemaModalHeader>
              <div
                style={{
                  color: '#cbd5e1',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto'
                }}
              >
                <p style={{ margin: '0 0 1rem', color: '#94a3b8' }}>
                  Listing symbol used for spot-style trading and batch economics for this class.
                </p>
                <div style={{ display: 'grid', gap: '0.65rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Quote style</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {marketModalClass.marketSymbol} / USD
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Trading unit</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.tradingUnit || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Token standard</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.tokenStandard || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Batches</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.totalBatches ?? 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Class status</span>
                    <span style={{ color: marketModalClass.isActive ? '#4ade80' : '#fbbf24' }}>
                      {marketModalClass.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {(() => {
                    const pair = getMarketPairForAssetClass(marketModalClass.id);
                    if (!pair) {
                      return (
                        <p style={{ margin: '1rem 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                          No separate market-pair record is registered in the app for this class yet; the symbol above
                          still identifies how this class is quoted alongside others.
                        </p>
                      );
                    }
                    return (
                      <div
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.75rem 1rem',
                          background: 'rgba(79, 172, 254, 0.08)',
                          borderRadius: '8px',
                          border: '1px solid rgba(79, 172, 254, 0.25)'
                        }}
                      >
                        <div style={{ fontSize: '0.8rem', color: '#7dd3fc', marginBottom: '0.35rem' }}>
                          Registered pair
                        </div>
                        <pre style={{ margin: 0, fontSize: '0.78rem', color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(pair, null, 2)}
                        </pre>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </SchemaViewModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewSchemaTemplate && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewSchemaTemplate(null)}
          >
            <SchemaViewModalSurface
              initial={{ scale: 0.92, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 12 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              <SchemaModalHeader>
                <div>
                  <h2>{viewSchemaTemplate.name}</h2>
                  <div className="meta">
                    {viewSchemaTemplate.category} · {viewSchemaTemplate.classType} · {viewSchemaTemplate.marketSymbol} ·{' '}
                    {viewSchemaTemplate.tradingUnit}
                  </div>
                </div>
                <ActionButton type="button" onClick={() => setViewSchemaTemplate(null)}>
                  Close
                </ActionButton>
              </SchemaModalHeader>
              <SchemaScrollArea ref={schemaScrollRef}>
                <SchemaCode dangerouslySetInnerHTML={{ __html: schemaViewPayload.html }} />
              </SchemaScrollArea>
            </SchemaViewModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ManagerContainer>
  );
}

export default AssetClassManager; 